import React, {useCallback, useMemo} from "react";
import {FieldErrors, useForm} from "react-hook-form";
import {
    ErrorMessage,
    flattenSchema,
    getErrorFeedback,
    getFormPermissions,
    getLabel,
    parseRuleType,
    renderComponent,
    scrollToError,
    setRuleType,
    SUBMIT_MODE
} from "./dyanimcFormUtil.tsx";
import {
    CUSTOM_ERROR_TYPE,
    DataGridComponent,
    FormComponent,
    FormSchema,
    FormType,
    FormValues, SelectComponent,
    ValidationRuleLevel
} from "./DynamicFormTypes.ts";
import {FormToolbarV2} from "./FormToolbarV2.tsx";
import {DynamicFormProvider} from "./DyanmicFormContext.tsx";
import {FormPermissions, FormSubmission, QCDecision, Trigger} from "../../api/ApiTypes.ts";
import {useFormApi} from "../../context/ApiContext.tsx";
import {useNavigate} from "react-router-dom";
import {NotificationType, useNotification} from "../../context/NotificationContext.tsx";
import {WarningConfirmationModal} from "../WarningModal.tsx";
import {generateComponentId} from "../../filehelper/fileutils.ts";
import {ValidationFactory} from "./ValidationFactory.ts";
import {useAuth} from "../../context/AuthContext.tsx";
import {FormChangesModal} from "./modals/FormChangesModal.tsx";
import {AggregateValidator, TriggerRequest} from "./fields/customvalidator/AggregateValidator.ts";
import {BackendOnlyValidator} from "./fields/customvalidator/BackendOnlyValidator.ts";
import {HeaderInfo} from "./HeaderInfoBar.tsx";
import {loadingManager} from "../../context/LoadingManager.ts";

interface FormRendererProps {
    schema: FormSchema;
    mode: SUBMIT_MODE;
    buttonText?: string;
    metaData: any;
    formSubmission?: FormSubmission;
    triggers?: Trigger[] | undefined;
    headerInfo?: HeaderInfo | undefined;
    formPermissions?: FormPermissions;
}

type QCModalResult = {
    decision: QCDecision;
    comment: string;
};

function processComponents(components: FormComponent[]): FormComponent[] {
    return components.map(component => {
        const processed: FormComponent = {
            ...component,
            componentId: generateComponentId(component)
        };

        if ('components' in processed && Array.isArray(processed.components)) {
            processed.components = processComponents(processed.components);
        }
        if (processed.type === FormType.DATAGRID
            && 'headerConfig' in processed
            && processed.headerConfig?.components) {
            processed.headerConfig.components = processComponents(processed.headerConfig.components);
        }
        return processed;
    });
}

function buildInitialValues(formSubmission: FormSubmission | undefined): FormValues | null {
    const values: FormValues = {};

    if (!formSubmission?.rawSubmit) {
        return values;
    }

    const lastValues = formSubmission.rawSubmit;

    for (const key in lastValues) {
        const value = lastValues[key];
        if (value === null || value === undefined) {
            continue;
        }
        if (Array.isArray(value) && value.length === 0) {
            continue;
        }
        values[key] = value;
    }

    return values;
}
// Build default values from schema components
function buildDefaultValues(formSchema: FormSchema, metaData: any): FormValues {
    const values: FormValues = {};

    const processComponent = (component: FormComponent) => {
        try {
            // Process nested components if this component type has them (not needed for datagrid here because the field key will be different)
            if ('components' in component && Array.isArray(component.components) && component.type !== FormType.DATAGRID) {
                component.components.forEach(processComponent);
            }

            //data grid header is actually just a ui component wrapper, so we do need to process it (even if we dont process the normal data grid)
            if (component.type == FormType.DATAGRID
                && 'headerConfig' in component
                && component.headerConfig?.components) {
                component.headerConfig.components.forEach(processComponent);
            }

            if (component.type === FormType.PANEL
                && "staticHeaders" in component
                && component.staticHeaders?.length) {
                for (const header of component.staticHeaders) {
                    if (header.fieldKey && header.display) {
                        values[header.fieldKey] = header.display;
                    }
                }
            }

            const defaultValue = getDefaultValue(component, metaData);
            if (defaultValue === undefined) {
                return; // Skip if no default value
            }
            values[component.fieldKey] = defaultValue;
        } catch (error) {
            console.log("error setting default value for component", component, error);
        }
    };

    if (!formSchema.components) {
        return values;
    }
    formSchema.components.forEach(processComponent);
    return values;
}

export const getDefaultValue = (component: FormComponent, metaData: any): any => {
    if (component.defaultValue?.value) {
        return component.defaultValue.value;
    }

    let metaDataKey = component.defaultValue?.metaDataKey

    switch (component.type) {
        case FormType.TEXTFIELD:
        case FormType.TEXTAREA: {
            return metaDataKey ? metaData[metaDataKey] : '';
        }
        case FormType.SIMPLE_DATE:
        case FormType.DATETIME:
        case FormType.CURRENCY:
        case FormType.NUMBER:
        case FormType.DERIVED:
        case FormType.RADIO: {
            return metaDataKey ? metaData[metaDataKey] : null;
        }
        case FormType.SELECT:{
            if (metaDataKey) {
                let asSelectComponent = component as SelectComponent;
                let metaDataValue =  metaData[metaDataKey].toString().toLowerCase();
                let option = asSelectComponent.provided?.find(x => x.value.toLowerCase() === metaDataValue);
                return JSON.stringify(option);
            }
            return null;
        }
        case FormType.CHECKBOX: {
            return metaDataKey ? metaData[metaDataKey] : false;
        }
        case FormType.DATAGRID:
            const defaultDatagridValue = [];
            const defaultAmount = "defaultAmount" in component && component.defaultAmount ? component.defaultAmount : 1;
            for (let i = 0; i < defaultAmount; i++) {
                defaultDatagridValue.push(getDefaultRowForDataGrid(component as DataGridComponent, metaData));
            }
            return defaultDatagridValue;
        default: {
            return metaDataKey ? metaData[metaDataKey] : undefined;
        }
    }
}

// Helper function to deep compare two objects and get differences
const getFormDifferences = (original: FormValues, current: FormValues): Record<string, { old: any; new: any }> => {
    const differences: Record<string, { old: any; new: any }> = {};

    // Get all keys from both objects
    const allKeys = new Set([...Object.keys(original), ...Object.keys(current)]);

    allKeys.forEach(key => {
        const originalValue = original[key];
        const currentValue = current[key];

        // Handle different types of comparison
        if (originalValue === currentValue) {
            return; // No difference
        }

        // Handle null/undefined cases
        if ((originalValue == null && currentValue == null)) {
            return; // Both null or undefined, consider equal
        }

        // Handle object/array comparison (deep comparison)
        if (typeof originalValue === 'object' && typeof currentValue === 'object' &&
            originalValue !== null && currentValue !== null) {
            if (JSON.stringify(originalValue) !== JSON.stringify(currentValue)) {
                differences[key] = { old: originalValue, new: currentValue };
            }
        } else if (originalValue !== currentValue) {
            // Primitive values that are different
            differences[key] = { old: originalValue, new: currentValue };
        }
    });

    return differences;
};

export const getDefaultRowForDataGrid = (component: DataGridComponent, metaData: any): FormValues => {
    const row: FormValues = {};
    component.components.forEach(field => {
        const defaultValue = getDefaultValue(field, metaData);
        if (defaultValue !== undefined) {
            row[field.fieldKey] = defaultValue;
        }
    });
    return row;
}
export const FormRenderer: React.FC<FormRendererProps> = (props) => {
    const { schema } = props;
    const formApi = useFormApi();


    const [triggers, setTriggers] = React.useState<Trigger[] | undefined>();
    const [ready, setReady] = React.useState(false);

    // key should be based on the *schema* that matters for backend triggers
    const backendKey = useMemo(() => JSON.stringify(schema), [schema]);

    React.useEffect(() => {
        console.log("use effect called b4 inner", {backendKey: backendKey});
        let cancelled = false;
        setReady(false);
        setTriggers(undefined);

        let request: TriggerRequest = {
            schema: schema
        }
        formApi.getCustomTriggers(request)
            .then((triggers) => {
                if (cancelled) return;
                setTriggers(triggers);
                setReady(true);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error("Error fetching custom triggers for backend validator", err);
                setReady(true); // hard-block if you truly refuse to render without it
            });

        return () => { cancelled = true; };
    }, [backendKey]);

    if (!ready) {
        return (
            <div className="p-4 border bg-gray-50 rounded text-sm">
                initializing form…
            </div>
        );
    }


    return (
        <FormRendererInner
            {...props}
            triggers={triggers}
            // optional: if schema changes, hard reset whole form
            key={backendKey}
        />
    );
};

export const FormRendererInner: React.FC<FormRendererProps> = ({
                                                                   schema,
                                                                   mode,
                                                                   buttonText = 'Submit Form',
                                                                   metaData = {},
                                                                   formSubmission,
                                                                   triggers = undefined,
                                                                   headerInfo = undefined,
                                                                   formPermissions: externalPermissions
                                                               }) => {
    const formApi = useFormApi();
    const navigate = useNavigate();
    const notification = useNotification();
    const auth = useAuth();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [changesModalState, setChangesModalState] = React.useState<{
        isOpen: boolean;
        differences: Record<string, { old: any; new: any }>;
        comment: string;
        resolver?: (value: QCModalResult) => void;
    }>({
        isOpen: false,
        differences: {},
        comment: ""
    });


    const [warningModalState, setWarningModalState] = React.useState<{
        isOpen: boolean;
        warnings: ErrorMessage[];
    }>({
        isOpen: false,
        warnings: []
    });

    const [manualTrigger, setManualTrigger] = React.useState(0);
    const formPermissions = useMemo<FormPermissions> (() => {
        if (externalPermissions) {
            console.info("using externally provided form permissions", externalPermissions);
            return externalPermissions;
        }
        const permissions =  getFormPermissions(mode, formSubmission, auth.me?.userType);
        console.info("calculating form permissions", permissions);
        return permissions;
    }, [mode, formSubmission?.id, auth.me?.userType, externalPermissions]);

    // Process schema - memoized to prevent unnecessary recalculations
    const processedSchema = useMemo(() => {
        if (!schema.components) {
            return schema;
        }
        return {
            ...schema,
            components: processComponents(schema.components)
        }
    }, [schema]);


    const flattenedSchemaMap = useMemo(() => flattenSchema(processedSchema), [processedSchema]);

    // Initialize react-hook-form with memoized default values
    const defaultValues = useMemo(() => {
        console.log("building default values from schema");
        return buildDefaultValues(processedSchema, metaData)
    }, [processedSchema, metaData]);

    // Initialize react-hook-form with memoized default values
    const jumpToList = useMemo(() => {
        console.log("building jump To list from schema");
        return Array.from(flattenedSchemaMap.values()).filter(component => component.type === FormType.PANEL);
    }, [flattenedSchemaMap, metaData]);

    const customValidator = useMemo<AggregateValidator | undefined>(() => {
        if (!triggers || triggers.length === 0) {
            return undefined;
        }
        return new BackendOnlyValidator(triggers, formApi, schema,  metaData);
    }, [processedSchema]);

    const initialValues = useMemo(() => {
        console.info("building initial values from form submission")
        return buildInitialValues(formSubmission);
    }, [formSubmission]);

    const actualDefaultsToProvideToForm = useMemo(() => {
        const merged =  {...defaultValues, ...initialValues};
        console.log("merged default values", merged);
        return merged;
    }, [initialValues, defaultValues]);

    const formMethods = useForm<FormValues>({
        defaultValues: actualDefaultsToProvideToForm, // Merge default values from schema and initial values from submission
        shouldUnregister: false, // Keep unregistered fields in the form state
        reValidateMode: "onBlur",
        mode: "onBlur"
    });

    const triggerValidationUpdate = useCallback(async () => {
        await formMethods.trigger();
        setManualTrigger(prev => prev + 1);
    }, [formMethods]);


    const showChangesModal = useCallback(
        (differences: Record<string, { old: any; new: any }>): Promise<QCModalResult> => {
            return new Promise((resolve) => {
                setChangesModalState({
                    isOpen: true,
                    differences,
                    comment: "",
                    resolver: resolve
                });
            });
        },
        []
    );
    const closeChangesModal = useCallback(() => {
        setChangesModalState({ isOpen: false, differences: {}, comment: "", resolver: undefined });
    }, []);


    const processValidationAndDecide = useCallback(async (errors: FieldErrors<FormValues>) => {
        //even though we have the errorFeedback already, we need to recalculate it here just in case there is async issues
        console.log("submitted values", formMethods.getValues());
        const extraCheck = getErrorFeedback(errors, flattenedSchemaMap);
        if (extraCheck.nonNestedErrors.length > 0) {
            console.log("hard stops", extraCheck.nonNestedWarnings);
            notification.showNotification(NotificationType.ERROR, "Form has hard stops, please fix the errors before submitting.");
            return;
        }

        if (extraCheck.nonNestedWarnings.length > 0) {
            setWarningModalState({
                isOpen: true,
                warnings: extraCheck.nonNestedWarnings
            });
            return;
        }
        await performActualSubmit();
    }, [formPermissions?.submitMode]);

    const handleConfirmWarnings = async () => {
        setWarningModalState({
            isOpen: false,
            warnings: []
        })
        await performActualSubmit();
    }

    const handleCancelWarnings = () => {
        setWarningModalState({
            isOpen: false,
            warnings: []
        })
    }

    const onValidSubmit = useCallback(async (data: FormValues) => {
        console.log(data);
        await performActualSubmit();
    }, [formPermissions?.submitMode]);

    const onInvalidSubmit = useCallback(async (errors: FieldErrors<FormValues>) => {
        console.log("Form has errors:", errors);
        await processValidationAndDecide(errors);
    }, [processValidationAndDecide, formPermissions?.submitMode]);

    const trunicateDataGridValues = async () =>  {
        //this nasty masterpeice shuold remove extra invalid rows from data grids if they already have a minimum number of rows filled out
        //this is a fucntion that will delete rows from the data grid if that data grid has
        //more than the minumum number of rows filled out and the extra rows are not more valid than the threshold percentage
        //only allow trunicate for first submit by appraiser
        if (formPermissions.submitMode !== SUBMIT_MODE.APPRAISER_SUBMIT) {
            console.warn("Data Grid values will not be trunicated. trunucateDataGridValues only allowed on first appraiser submit", formPermissions);
            return;
        }
        const THRESHOLD_PERCENT = 50;//half fields filled out

        const allDataGridComponents = Array.from(flattenedSchemaMap.values())
            .filter((component: any) => {
                return component.type === FormType.DATAGRID;
            }) as DataGridComponent[];

        try {
            for (const component of allDataGridComponents) {
                const dg = component as DataGridComponent;
                await formMethods.trigger(dg.fieldKey);
                let fieldState = formMethods.getFieldState(dg.fieldKey);
                if (!fieldState.error) {
                    continue; //no error, no need to trunucate
                }
                let min = ValidationFactory.getMinValue(dg, 0);
                let allRows = formMethods.getValues(dg.fieldKey);
                if (!Array.isArray(allRows) || allRows.length <= min || !Array.isArray(fieldState.error)) {
                    continue; //no need to trunicate if its not an array or if it doesnt even have enough rows
                }

                let dgErrors = fieldState.error ?? [];
                //count of fields that have validation rules
                const countOfValidatedFields = dg.components.reduce(
                    (count, field) =>
                        count +
                        (field.validationRules?.some(rule => rule.level !== ValidationRuleLevel.WARNING) ? 1 : 0),
                    0
                );
                if (countOfValidatedFields === 0) {
                    continue; //if no fields that have error validation rules, no need to trunicate
                }

                let failedIndexes: number[] = [];
                allRows.forEach((_row, index) => {
                    //check for errors
                    let errorsForRow = dgErrors[index] || undefined;
                    if (!errorsForRow) {
                        return;
                    }
                    let errorCount = 0;
                    Object.values(errorsForRow).forEach((value) => {
                        if (typeof value === 'object' && value !== null && 'type' in value) {
                            const castedValue = value as { type?: string };
                            const info = parseRuleType(castedValue.type);
                            if (info.type === CUSTOM_ERROR_TYPE.FACTORY &&
                                info.level !== ValidationRuleLevel.WARNING) {
                                errorCount++;
                            }
                        }
                    });
                    if (errorCount > 1 && errorCount / countOfValidatedFields * 100 >= THRESHOLD_PERCENT) {
                        failedIndexes.push(index);
                    }
                });
                if (failedIndexes.length === 0) {
                    console.log("no rows to trunucate for data grid", dg.fieldKey);
                    continue; //no rows to trunucate
                }

                if (allRows.length - failedIndexes.length < min) {
                    console.log("not enough rows left after trunucation for data grid", dg.fieldKey, "remaining rows", allRows.length - failedIndexes.length, "min required", min);
                    continue; //not enough rows left after trunucation
                }

                //remove the rows that failed validation
                console.log("removing rows from data grid", dg.fieldKey, "failed indexes", failedIndexes);
                allRows = allRows.filter((_, index) => !failedIndexes.includes(index));
                formMethods.setValue(dg.fieldKey, allRows);
            }
        }
        catch (error) {
            console.error(error);
            return;
        }
    }

    const handleSubmissionAttempt = async () => {
        setIsSubmitting(true);
        try {
            //remove any extra rows from data grids that are not valid
            await trunicateDataGridValues();
            //show errors
            await formMethods.trigger();
            //call handle submit which will validate the form and call onValidSubmit or onInvalidSubmit
            await formMethods.handleSubmit(onValidSubmit, onInvalidSubmit)();
        }
        catch (error) {
            console.error('Error during form submission:', error);
        }
        finally {
            setIsSubmitting(false);
        }

    }


    const performActualSubmit = useCallback(async () => {
        try {
            setIsSubmitting(true);
            const currentValues = formMethods.getValues();
            if (!formPermissions.canEdit) {
                console.warn("Submission blocked, form not allowed to be submitted", formPermissions);
                notification.showNotification(NotificationType.ERROR, "Form not allowed to be submitted, please contact I.T.");
                return;
            }
            let qcDecision: QCDecision | undefined = undefined;
            let gblComment: string | undefined = undefined;

            //if its qc or if its not appraiser or appraiser admin, show the changes modal if there are changes
            if (formPermissions.submitMode === SUBMIT_MODE.QC) {
                // ALWAYS show modal in QC mode even if there is no prior rawSubmit
                const original = formSubmission?.rawSubmit ?? {};
                const differences = getFormDifferences(original, currentValues);
                console.log("Form differences:", differences);

                const { decision, comment } = await showChangesModal(differences);
                if (decision === "CANCEL") {
                    console.log("user cancelled submission from changes modal");
                    return;//user cancelled
                }
                qcDecision = decision;
                gblComment = comment;
            }

            if (formPermissions.submitMode === SUBMIT_MODE.ADMIN) {
                console.log("submitting as admin, skipping actual submit!");
                notification.showNotification(NotificationType.SUCCESS, "In Admin Mode, submit does nothing!");
                return;
            }
            else if (formPermissions.submitMode !== SUBMIT_MODE.APPRAISER_SUBMIT && formPermissions.submitMode !== SUBMIT_MODE.QC) {
                console.warn("Submission blocked, unknown submit mode", formPermissions);
                notification.showNotification(NotificationType.ERROR, "Form not allowed to be submitted, please contact I.T.");
                return;
            }

            loadingManager.show();

            //todo probably should make a different submit call for qc vs appraiser so we can track that better
            console.log("submitting as ", formPermissions.submitMode, "permisssions: ", formPermissions);
            const submissionResponse = await formApi.submitForm(
                metaData.formDefinitionId,
                metaData.orderId,
                currentValues,
                formPermissions.submitMode,
                qcDecision,
                gblComment
            );

            if (submissionResponse.ok) {
                notification.showNotification(NotificationType.SUCCESS, "Form submitted successfully");
                if (formPermissions.submitMode === SUBMIT_MODE.QC && qcDecision) {
                    window.close();
                    return;
                }
                navigate(`/orderdetails/${metaData.orderId}`);
                return;
            }
            else if (submissionResponse.fieldErrors) {
                let errorNotification = "";
                try {
                    submissionResponse.fieldErrors.forEach((error, index) => {
                        const fieldKey = error.field;
                        const errorMessage = error.error;
                        formMethods.setError(fieldKey, {
                            //todo this is where we would set warnings and such but for now default it to error
                            type: setRuleType(ValidationRuleLevel.ERROR, CUSTOM_ERROR_TYPE.BACKEND), //set the error to BACKEND so it will still allow to be submitted (we dont count an error if it has type "BACKEND") (check getErrorFeeback)
                            message: errorMessage
                        });

                        if (index == 0) {
                            const label = getLabel(fieldKey, flattenedSchemaMap);
                            errorNotification += `${label}: ${errorMessage}\n`;
                            scrollToError({
                                label: label,
                                fieldKey: fieldKey,
                                message: errorMessage,
                                level: ValidationRuleLevel.ERROR,
                            })
                        }
                    });
                }
                catch (error) {
                    console.error(error);
                }
                // Handle field errors
                notification.showNotification(NotificationType.ERROR, errorNotification);
            }
            console.log("submission response", submissionResponse);
        } catch (error) {
            console.error('Error submitting form:', error);
        }
        finally {
            loadingManager.hide();
            setIsSubmitting(false);
        }
    }, [metaData, formPermissions.submitMode]);

    const bootReady = useMemo(() => {
        // if your schema can be empty, that’s still “ready”
        const schemaReady = processedSchema && processedSchema?.components && Array.isArray(processedSchema.components) && processedSchema.components.length >= 0;
        const defaultsReady = defaultValues !== undefined && defaultValues !== null;
        const methodsReady = !!formMethods;
        const needsValidator = processedSchema?.components?.some(c => c.type === FormType.CUSTOM_VALIDATOR) || (triggers && triggers.length > 0);

        const validatorReady = !needsValidator || (needsValidator && !!customValidator);
        const formPermissionsReady = !!formPermissions;
        const bootReady = schemaReady && defaultsReady && methodsReady && validatorReady && formPermissionsReady;
        console.log("bootReady?", {schemaReady, defaultsReady, methodsReady, validatorReady, bootReady});
        return bootReady;
    }, [processedSchema, defaultValues, formMethods, customValidator, formPermissions]);

    const renderedComponents = useMemo(() => {
        if (!bootReady || !processedSchema.components?.length) return null;
        console.log("rerendering all ocmponents!!!", bootReady);
        return processedSchema.components.map(component =>
            renderComponent({ component })
        );
    }, [bootReady, processedSchema]);

    return (
        <>
            <DynamicFormProvider
                metaData={metaData}
                formMethods={formMethods}
                schema={processedSchema}
                defaultValues={defaultValues}
                triggerValidationUpdate={triggerValidationUpdate}
                flattenedSchemaMap={flattenedSchemaMap}
                customValidator={customValidator}
                formPermissions={formPermissions}
            >
                {/* The FormToolbar is now rendered outside the form element */}
                <FormToolbarV2
                    manualTrigger={manualTrigger}
                    jumpToList={jumpToList}
                    headerInfo={headerInfo}
                />
                <form className="w-full min-w-[1000px] overflow-x-visible bg-white">
                    <fieldset disabled={isSubmitting} className={`w-full min-w-0 ${isSubmitting ? 'opacity-50' : ''}`}>
                        {!bootReady && (
                            <div className="p-4 border bg-gray-50 rounded text-sm">
                                initializing form…
                            </div>
                        )}
                        {bootReady && (
                            <div className="w-full min-w-0 space-y-4">
                                {renderedComponents}
                            </div>
                        )}
                        <div className="mt-6">
                            <button
                                type="button"
                                onClick={handleSubmissionAttempt}
                                className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${
                                    isSubmitting ? 'animate-pulse' : ''
                                }`}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Submitting...' : buttonText}
                            </button>
                        </div>
                    </fieldset>
                </form>

            </DynamicFormProvider>
            {/* Render the WarningConfirmationModal */}
            <WarningConfirmationModal
                isOpen={warningModalState.isOpen}
                warnings={warningModalState.warnings}
                onConfirm={handleConfirmWarnings}
                onClose={handleCancelWarnings}
            />
            <FormChangesModal
                isOpen={changesModalState.isOpen}
                differences={changesModalState.differences}
                comment={changesModalState.comment}
                onCommentChange={(comment) =>
                    setChangesModalState((s) => ({ ...s, comment }))
                }
                onDecision={(decision) => {
                    changesModalState.resolver?.({ decision, comment: changesModalState.comment });
                    closeChangesModal();
                }}
                flattenedSchemaMap={flattenedSchemaMap}
                simplified={true}
            />
        </>

    );
};
